/**
 * Change service's one body: the POST to the engagement route that sets
 * the service, the lead SPICE therapist and the lead care manager together.
 *
 *   - engagement, always;
 *   - spice_therapist_roster_id, always: the SPICE therapist box's choice
 *     when the box is shown (the service includes SPICE), the stored
 *     therapist when it is hidden, so a pick made and then hidden is
 *     dropped and nothing hidden can be refused; '' (No SPICE therapist)
 *     is null;
 *   - primary_cm_roster_id only when the Care manager box is shown (the
 *     service includes care management and the viewer is not SPICE-only)
 *     and its choice differs from the lead on file; '' (No care manager)
 *     is null. Absent, the route leaves the care manager as it is.
 *
 * Nothing changed is null: the dialog closes and sends nothing.
 */

import type { ClientEngagement } from '@/lib/clientRecord';

export interface ChangeServiceLeads {
  engagement: ClientEngagement;
  primaryCmRosterId: string | null;
  spiceTherapistRosterId: string | null;
}

export interface ChangeServiceChoice {
  engagement: ClientEngagement;
  /** The select's value: '' for No care manager. */
  primaryCmRosterId: string;
  /** The select's value: '' for No SPICE therapist. */
  spiceTherapistRosterId: string;
}

export interface ChangeServiceBody {
  engagement: ClientEngagement;
  spice_therapist_roster_id: string | null;
  primary_cm_roster_id?: string | null;
}

export function includesCareManagement(engagement: ClientEngagement): boolean {
  return engagement === 'care_management' || engagement === 'both';
}

export function includesSpice(engagement: ClientEngagement): boolean {
  return engagement === 'spice' || engagement === 'both';
}

/** Whether the Care manager box shows: the service includes care
 *  management and the viewer is not SPICE-only. */
export function showsCareManagerBox(engagement: ClientEngagement, spiceOnlyViewer: boolean): boolean {
  return includesCareManagement(engagement) && !spiceOnlyViewer;
}

export function changeServiceBody(input: {
  stored: ChangeServiceLeads;
  chosen: ChangeServiceChoice;
  spiceOnlyViewer: boolean;
}): ChangeServiceBody | null {
  const { stored, chosen, spiceOnlyViewer } = input;
  const therapist = includesSpice(chosen.engagement)
    ? chosen.spiceTherapistRosterId === ''
      ? null
      : chosen.spiceTherapistRosterId
    : stored.spiceTherapistRosterId;
  const body: ChangeServiceBody = { engagement: chosen.engagement, spice_therapist_roster_id: therapist };
  let changed = chosen.engagement !== stored.engagement || therapist !== stored.spiceTherapistRosterId;
  if (showsCareManagerBox(chosen.engagement, spiceOnlyViewer)) {
    const cm = chosen.primaryCmRosterId === '' ? null : chosen.primaryCmRosterId;
    if (cm !== stored.primaryCmRosterId) {
      body.primary_cm_roster_id = cm;
      changed = true;
    }
  }
  return changed ? body : null;
}

export interface LeadOption {
  roster_id: string;
  name: string;
}

/** A lead select's people: the list, with the lead on file added first
 *  when the list does not hold her (departed, or no longer doing the
 *  service), so the select shows the truth. */
export function withLead(
  list: readonly LeadOption[],
  lead: { rosterId: string; displayName: string } | null,
): LeadOption[] {
  if (lead === null || list.some((p) => p.roster_id === lead.rosterId)) return [...list];
  return [{ roster_id: lead.rosterId, name: lead.displayName }, ...list];
}
